/**
 * Calendar grid + time input for DateTimePicker popover.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { addMonths, format, subMonths } from 'date-fns';

import { cn } from '@/lib/utils';

import { WEEKDAYS } from './date-time-picker-utils';

export interface DateTimePickerContentProps {
  viewMonth: Date;
  setViewMonth: (fn: (m: Date) => Date) => void;
  calendarDays: Date[];
  date: Date | null;
  timeStr: string;
  isDayDisabled: (d: Date) => boolean;
  onDaySelect: (d: Date) => void;
  onTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  compact?: boolean;
  minTime?: string;
  timezoneLabel?: string;
}

export const DateTimePickerContent: FC<DateTimePickerContentProps> = ({
  viewMonth,
  setViewMonth,
  calendarDays,
  date,
  timeStr,
  isDayDisabled,
  onDaySelect,
  onTimeChange,
  compact = false,
  minTime,
  timezoneLabel,
}) => {
  const navClass = compact ? 'h-7 w-7' : 'h-9 w-9';
  const dayClass = compact ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm';
  const gapClass = compact ? 'gap-2' : 'gap-3';
  return (
    <div className={cn('flex flex-col', gapClass)}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setViewMonth((m) => subMonths(m, 1))}
          className={cn('flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring', navClass)}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className={compact ? 'text-xs font-medium text-foreground' : 'text-sm font-medium text-foreground'}>
          {format(viewMonth, 'MMMM yyyy')}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth((m) => addMonths(m, 1))}
          className={cn('flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring', navClass)}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className={cn('py-1 font-medium text-muted-foreground', compact ? 'text-[10px]' : 'text-xs')}>
            {wd}
          </div>
        ))}
        {calendarDays.map((d) => {
          const inMonth =
            d.getMonth() === viewMonth.getMonth() && d.getFullYear() === viewMonth.getFullYear();
          const today =
            d.getDate() === new Date().getDate() &&
            d.getMonth() === new Date().getMonth() &&
            d.getFullYear() === new Date().getFullYear();
          const selected =
            date &&
            d.getDate() === date.getDate() &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear();
          const dayDisabled = isDayDisabled(d);
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={dayDisabled}
              onClick={() => !dayDisabled && onDaySelect(d)}
              className={cn(
                'rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                dayClass,
                !inMonth && 'text-muted-foreground opacity-50',
                inMonth && !selected && 'text-foreground hover:bg-accent hover:text-accent-foreground',
                selected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                today && !selected && 'font-semibold text-primary',
                dayDisabled && 'cursor-not-allowed opacity-40',
              )}
            >
              {format(d, 'd')}
            </button>
          );
        })}
      </div>
      <div className={cn('flex items-center gap-2 border-t border-border', compact ? 'pt-2' : 'pt-3')}>
        <label htmlFor="dtp-time" className={cn('shrink-0 font-medium text-muted-foreground', compact ? 'text-[10px]' : 'text-xs')}>
          {timezoneLabel ? `Time (${timezoneLabel})` : 'Time'}
        </label>
        <input
          id="dtp-time"
          type="time"
          value={timeStr}
          min={minTime}
          onChange={onTimeChange}
          className={cn(
            'flex-1 rounded-md border border-input bg-background text-foreground',
            compact ? 'h-7 px-2 py-1 text-xs' : 'h-9 px-3 py-1.5 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          )}
          aria-label="Time"
        />
      </div>
    </div>
  );
};
